import type { Order } from '../domain/entities/Order.js';
import type { ProductMix, ProductMixSlice } from '../domain/entities/Product.js';
import type { Period } from '../domain/entities/KPI.js';
import type { Season } from '../domain/entities/Season.js';
import type { DashboardFilters } from '../domain/entities/DashboardView.js';
import type { SeasonalActualRow, SeasonalTargetRow } from '../domain/sources/sources.js';
import { previousSeason } from '../domain/entities/Season.js';
import { windowFor } from './kpi.js';

/**
 * Aggregate orders within the period along two product axes:
 *   - byCategory (Tops, Bottoms, Outerwear, ...)
 *   - byLine     (MAN, WOMAN, JUNIOR, KIDS, ...)
 *
 * Per-order lines are preferred when available; otherwise the header-level
 * `category`/`line` on the order is used. This handles both formats:
 * line-itemized orders and pre-aggregated order rows (as our snapshot returns).
 */
export function buildProductMix(orders: Order[], period: Period, now = new Date()): ProductMix {
  const w = windowFor(period, now);
  const inWindow = orders.filter((o) => {
    const t = new Date(o.date).getTime();
    return t >= w.fromCurrent.getTime() && t < w.toCurrent.getTime();
  });

  const byCategoryAcc = new Map<string, MixAcc>();
  const byLineAcc = new Map<string, MixAcc>();

  const bump = (map: Map<string, MixAcc>, key: string, eur: number, units: number, clientId: string) => {
    if (!key) return;
    const cur = map.get(key) ?? emptyAcc();
    cur.revenueEUR += eur;
    cur.units += units;
    if (clientId) cur.clients.add(clientId);
    map.set(key, cur);
  };

  for (const o of inWindow) {
    if (o.lines.length > 0) {
      for (const line of o.lines) {
        const eur = line.quantity * line.unitPriceEUR;
        bump(byCategoryAcc, line.category ?? o.category ?? '(sin categoría)', eur, line.quantity, o.clientId);
        bump(byLineAcc, line.line ?? o.line ?? '(sin línea)', eur, line.quantity, o.clientId);
      }
    } else {
      // Header-level fallback when the order has no itemized lines.
      const units = o.lines.reduce((s, l) => s + l.quantity, 0);
      bump(byCategoryAcc, o.category ?? '(sin categoría)', o.totalEUR, units, o.clientId);
      bump(byLineAcc, o.line ?? '(sin línea)', o.totalEUR, units, o.clientId);
    }
  }

  return {
    byCategory: toSortedSlices(byCategoryAcc),
    byLine: toSortedSlices(byLineAcc),
    // Seasonal donut slots — populated by buildSeasonalLineMixes() in the use
    // case. The period-relative mix doesn't know about seasons, so these stay
    // empty here; callers that want comparison donuts must merge in the
    // seasonal mixes.
    byLineCurrent: [],
    byLineLastSeason: [],
    byLineTarget: [],
  };
}

interface MixAcc { revenueEUR: number; units: number; clients: Set<string> }
function emptyAcc(): MixAcc { return { revenueEUR: 0, units: 0, clients: new Set() }; }

function toSortedSlices(acc: Map<string, MixAcc>): ProductMixSlice[] {
  const total = Array.from(acc.values()).reduce((s, v) => s + v.revenueEUR, 0);
  return Array.from(acc.entries())
    .map(([key, v]) => ({
      key,
      revenueEUR: Math.round(v.revenueEUR * 100) / 100,
      units: v.units,
      clients: v.clients.size,
      share: total > 0 ? Math.round((v.revenueEUR / total) * 1000) / 1000 : 0,
    }))
    .sort((a, b) => b.revenueEUR - a.revenueEUR);
}

/**
 * Apply line/category filter (line filter is ignored on purpose — the mix BY
 * LINE wouldn't make sense if we'd already filtered to a single line).
 */
function matchesCategoryFilter(row: { category: string }, f: DashboardFilters): boolean {
  if (f.category && row.category !== f.category) return false;
  return true;
}

/**
 * Build agent + season scoped revenue mix by line, returning a sorted slice
 * list. Used for current and last-season comparison donuts.
 */
function buildLineMixFromActuals(
  actuals: SeasonalActualRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
): ProductMixSlice[] {
  const acc = new Map<string, MixAcc>();
  for (const r of actuals) {
    if (r.agenteId !== agentId) continue;
    if (r.season !== season) continue;
    if (!matchesCategoryFilter(r, filters)) continue;
    const cur = acc.get(r.line) ?? emptyAcc();
    cur.revenueEUR += r.revenueEUR;
    cur.units += r.units;
    cur.clients.add(r.clienteId);
    acc.set(r.line, cur);
  }
  return toSortedSlices(acc);
}

/**
 * Same shape but reading the targets table — used for the "Target" donut.
 */
function buildLineMixFromTargets(
  targets: SeasonalTargetRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
): ProductMixSlice[] {
  const acc = new Map<string, MixAcc>();
  for (const r of targets) {
    if (r.agenteId !== agentId) continue;
    if (r.season !== season) continue;
    if (!matchesCategoryFilter(r, filters)) continue;
    const cur = acc.get(r.line) ?? emptyAcc();
    cur.revenueEUR += r.revenueTargetEUR;
    cur.units += r.unitsTarget;
    cur.clients.add(r.clienteId);
    acc.set(r.line, cur);
  }
  return toSortedSlices(acc);
}

/**
 * Build the three comparable line mixes for the donut comparison:
 *   - byLineCurrent     this season's actuals
 *   - byLineLastSeason  N-1 same season's actuals
 *   - byLineTarget      this season's target (N-1 × uplift)
 */
export function buildSeasonalLineMixes(
  actuals: SeasonalActualRow[],
  targets: SeasonalTargetRow[],
  agentId: string,
  season: Season,
  filters: DashboardFilters,
): Pick<ProductMix, 'byLineCurrent' | 'byLineLastSeason' | 'byLineTarget'> {
  const ly = previousSeason(season);
  return {
    byLineCurrent: buildLineMixFromActuals(actuals, agentId, season, filters),
    byLineLastSeason: ly
      ? buildLineMixFromActuals(actuals, agentId, ly, filters)
      : [],
    byLineTarget: buildLineMixFromTargets(targets, agentId, season, filters),
  };
}
